import "./Products.css";

/**
 * E8 Signage — LED glow sign boards and P10 text displays.
 * Reached by URL only (/products); nothing on the home page links here.
 *
 * The card pictures are hand-built SVG mock-ups served from public/img/products.
 * They stand in for studio photography: each is referenced by a fixed path, so
 * a real photo replaces one by overwriting the file with the same name (keep a
 * landscape 16:10 crop — the card frame uses aspect-ratio 16/10 with
 * object-fit: cover) and changing the one `image:` path in PRODUCTS below.
 */

// ── Contact ─────────────────────────────────────────────────────────────────
// The same numbers and address the site footer carries, so an enquiry from this
// page reaches the same desk as one from the home page.
const PHONE_DISPLAY = "+91 91639 54634";
const PHONE_HREF = "tel:+919163954634";
const WHATSAPP_HREF = "https://wa.me/919163954634";
const EMAIL = "support@eeighttech.com";

const E8_MARK = "/img/e8-mark.png";

// ── Catalogue ───────────────────────────────────────────────────────────────
type Product = {
	name: string;
	tag: string;
	blurb: string;
	sizes: string;
	price: string;
	points: string[];
	image: string;
};

/**
 * Prices are indicative shop-front rates for a single board, fabricated and
 * fitted, and are banded rather than fixed: the same board costs more as it
 * grows, and lettering, frame material and mounting move it further. Every
 * band lands inside the ₹4,000 – ₹25,000 range the page quotes.
 */
const PRODUCTS: Product[] = [
	{
		name: "Acrylic LED Glow Sign Board",
		tag: "Shop name board",
		blurb:
			"Backlit acrylic face on a slim aluminium frame. The everyday shop-front sign — evenly lit, weather-sealed, and readable from across the road.",
		sizes: "3 ft – 6 ft",
		price: "₹4,000 – ₹9,500",
		points: [
			"5 mm acrylic face, printed or cut vinyl",
			"Even LED edge-lighting, no hot spots",
			"Single-phase, 20–30 W per sq ft",
		],
		image: "/img/products/glow-acrylic.svg",
	},
	{
		name: "Backlit Flex Glow Sign",
		tag: "Budget friendly",
		blurb:
			"A lit flex sheet stretched over a box frame. The cheapest way to get a bright, full-colour shop board where the budget is tight and the message is simple.",
		sizes: "4 ft – 8 ft",
		price: "₹5,500 – ₹13,000",
		points: [
			"Backlit flex, solvent-printed, full colour",
			"Box frame in MS or aluminium",
			"Best value for wide, simple boards",
		],
		image: "/img/products/glow-backlit.svg",
	},
	{
		name: "LED Channel Letters",
		tag: "Premium 3D",
		blurb:
			"Individual three-dimensional letters with an internal lit face. The sign reads as a brand rather than a board, and the letters can be front-lit or halo-lit.",
		sizes: "4 ft – 10 ft",
		price: "₹9,000 – ₹18,000",
		points: [
			"CNC-cut 3D letters, acrylic or metal face",
			"Front-lit, back-lit or halo glow",
			"Runs cool, rated for 8–12 years",
		],
		image: "/img/products/channel-letters.svg",
	},
	{
		name: "Double-Sided Glow Sign",
		tag: "Hanging / pole",
		blurb:
			"One lit box, two readable faces — the board hangs from a canopy or a pole and is read from both directions of the street.",
		sizes: "6 ft – 12 ft",
		price: "₹12,000 – ₹22,000",
		points: [
			"Readable from both approaches",
			"Reinforced frame and drop rods",
			"Hanging, projecting or pole-mounted",
		],
		image: "/img/products/double-sided.svg",
	},
	{
		name: "P10 Single-Colour Text Display",
		tag: "Scrolling text",
		blurb:
			"A programmable red LED matrix for running messages — offers, timings, notices. Update the text from the phone in your pocket, no re-printing ever.",
		sizes: "3 ft – 10 ft",
		price: "₹4,500 – ₹14,000",
		points: [
			"10 mm pixel pitch, 1R module",
			"App over WiFi, or PC over USB",
			"Panels join for longer runs",
		],
		image: "/img/products/p10-single.svg",
	},
	{
		name: "P10 Full-Colour RGB Display",
		tag: "Video & graphics",
		blurb:
			"The full-colour version of the P10: 16.7 million colours, video and animation. Built for facade and shop-front displays where the screen is the attraction.",
		sizes: "8 ft – 20 ft",
		price: "₹14,000 – ₹25,000",
		points: [
			"10 mm pitch, SMD 3-in-1 RGB",
			"IP65 cabinets for outdoor facades",
			"~6,500 nits, 100,000-hour rated LEDs",
		],
		image: "/img/products/p10-rgb.svg",
	},
];

// ── Page ────────────────────────────────────────────────────────────────────
export default function Products() {
	return (
		<div className="products-route">
			<SiteBar />

			<header className="products-hero">
				<div className="products-wrap">
					<p className="products-eyebrow">E8 Signage</p>
					<h1>LED sign boards for shops that need to be seen</h1>
					<p className="products-lede">
						Glow sign boards, backlit acrylic and P10 LED displays —
						designed, fabricated and fitted to your shop front. From a
						single name board to a full-colour video screen.
					</p>

					<dl className="products-stats">
						<div>
							<dt>3 – 20 ft</dt>
							<dd>Board widths</dd>
						</div>
						<div>
							<dt>₹4,000 – ₹25,000</dt>
							<dd>Price range</dd>
						</div>
						<div>
							<dt>10 mm</dt>
							<dd>LED pixel pitch</dd>
						</div>
						<div>
							<dt>1,00,000 hrs</dt>
							<dd>Rated LED life</dd>
						</div>
					</dl>

					<div className="products-hero-actions">
						<a className="products-btn products-btn--solid" href={PHONE_HREF}>
							Call {PHONE_DISPLAY}
						</a>
						<a
							className="products-btn products-btn--ghost"
							href={WHATSAPP_HREF}
							target="_blank"
							rel="noreferrer"
						>
							WhatsApp your size
						</a>
					</div>
				</div>
			</header>

			<section className="products-section" id="catalogue">
				<div className="products-wrap">
					<h2 className="products-h2">What we make</h2>
					<p className="products-sub">
						Six standard builds. Anything in between — a longer run, a
						corner board, a logo cut to shape — is quoted on measurement.
					</p>

					<ul className="products-grid">
						{PRODUCTS.map((p) => (
							<ProductCard key={p.name} product={p} />
						))}
					</ul>
				</div>
			</section>

			<section className="products-section products-section--tint">
				<div className="products-wrap">
					<h2 className="products-h2">Why these last on a shop front</h2>
					<ul className="products-features">
						<li>
							<b>Built for the weather</b>
							Outdoor cabinets are IP65-sealed, with a shade suggested for
							direct-sun positions. Rain and dust stay on the outside.
						</li>
						<li>
							<b>Change the message, not the board</b>
							P10 displays are reprogrammed over WiFi from an Android app or
							USB from a PC, so an offer or a timing change takes minutes.
						</li>
						<li>
							<b>Grows with the shop</b>
							P10 panels connect in series and re-sync, so a 3 ft board can
							become a 20 ft run without starting over.
						</li>
						<li>
							<b>Low running cost</b>
							LED boards draw roughly 20–30 W per sq ft — a lit shop front
							that costs less to run than the tube light it replaced.
						</li>
					</ul>
				</div>
			</section>

			<section className="products-section" id="enquire">
				<div className="products-wrap">
					<div className="products-enquire">
						<div>
							<h2 className="products-h2">Get a price for your shop</h2>
							<p className="products-sub products-sub--tight">
								Send the width and height you have in mind, and whether the
								board is lit, acrylic or a P10 display. We'll come back with a
								fixed quote, the layout and a fitting date.
							</p>
							<ul className="products-contact">
								<li>
									<span>Call</span>
									<a href={PHONE_HREF}>{PHONE_DISPLAY}</a>
								</li>
								<li>
									<span>WhatsApp</span>
									<a href={WHATSAPP_HREF} target="_blank" rel="noreferrer">
										Message us
									</a>
								</li>
								<li>
									<span>Email</span>
									<a href={`mailto:${EMAIL}`}>{EMAIL}</a>
								</li>
								<li>
									<span>Workshop</span>
									RDB Boulevard, Sector V, Salt Lake, Kolkata 700091
								</li>
							</ul>
						</div>
						<div className="products-enquire-art" aria-hidden="true">
							<img src="/img/products/p10-single.svg" alt="" />
						</div>
					</div>
				</div>
			</section>

			<footer className="products-footer">
				<div className="products-wrap">
					<img src={E8_MARK} alt="" />
					<p>
						E8 Signage is a division of E8. Prices are indicative for a single
						fabricated and fitted board, exclusive of taxes and municipal
						signage licence, and are confirmed only in a written quote.
					</p>
				</div>
			</footer>
		</div>
	);
}

// ── Pieces ──────────────────────────────────────────────────────────────────

/** A slim bar rather than the home page's header, so this stand-alone page
 *  carries the mark and a way to call without importing the home navigation. */
function SiteBar() {
	return (
		<div className="products-bar">
			<div className="products-wrap products-bar-inner">
				<span className="products-brand">
					<img src={E8_MARK} alt="" />
					<b>E8 Signage</b>
				</span>
				<a className="products-bar-call" href={PHONE_HREF}>
					{PHONE_DISPLAY}
				</a>
			</div>
		</div>
	);
}

function ProductCard({ product }: { product: Product }) {
	return (
		<li className="products-card">
			<div className="products-card-media">
				<img src={product.image} alt={`${product.name} — ${product.tag}`} />
				<span className="products-card-tag">{product.tag}</span>
			</div>

			<div className="products-card-body">
				<h3>{product.name}</h3>
				<p className="products-card-blurb">{product.blurb}</p>

				<ul className="products-card-points">
					{product.points.map((point) => (
						<li key={point}>{point}</li>
					))}
				</ul>

				<div className="products-card-foot">
					<div>
						<span className="products-card-label">Size</span>
						<b>{product.sizes}</b>
					</div>
					<div>
						<span className="products-card-label">Price</span>
						<b className="products-card-price">{product.price}</b>
					</div>
				</div>

				<a className="products-card-cta" href={`${WHATSAPP_HREF}?text=${encodeURIComponent(`Hi E8, I'd like a quote for: ${product.name}`)}`} target="_blank" rel="noreferrer">
					Enquire about this board
				</a>
			</div>
		</li>
	);
}
